import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Api from '@services/api';
import ClearButton from './clear-button';
import Loader from './loader';
import Results from './results';
import { TextInput } from 'nhsuk-react-components';
import styles from './hcpc.module.scss';
import { validateHcpcRecord } from './hcpc.utils';

const Hcpc = forwardRef((props, ref) => {
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    const [data, setData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [focused, setFocused] = useState(-1);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [resultsVisible, setResultsVisible] = useState(false);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [acceptedRecord, setAcceptedRecord] = useState(null);
    const [validationReason, setValidationReason] = useState(null);

    const GMC_HCPC_ERROR = 'You cannot enter an HCPC PIN when a GMC PIN has been entered.';

    useEffect(() => {
        window.addEventListener('click', handleWindowClicked);
        setIsReadOnly(props.question.isReadOnly);

        return () => {
            window.removeEventListener('click', handleWindowClicked);
        };
    }, [props.question.isReadOnly]);

    useImperativeHandle(ref, () => ({
        validate,
        isValid,
        acceptedRecord,
        validationReason
    }));

    useEffect(() => {
        setSelectedRecord(props.question.acceptedRecord ?? null);
        setAcceptedRecord(props.question.acceptedRecord ?? null);
        setValidationReason(props.question.validationReason ?? null);
        setError(props.question.error);
        setIsValid(!!props.question.isValid);
    }, [
        props.question.acceptedRecord,
        props.question.validationReason,
        props.question.error,
        props.question.isValid
    ]);

    const hasGmcValue = () => {
        return !!String(props.gmcValue ?? '').trim();
    };

    const validate = (requireAccepted = true) => {
        const value = String(props.question.value ?? '').trim();
        const hasValue = value.length > 0;

        if (!hasValue) {
            setValidationError(undefined, true);
            return true;
        }

        if (hasGmcValue()) {
            setValidationError(GMC_HCPC_ERROR, false);
            return false;
        }

        if (requireAccepted && !acceptedRecord) {
            setValidationError(
                validationReason || 'Select a valid HCPC record from the list',
                false
            );
            return false;
        }

        setValidationError(undefined, true);
        return true;
    };

    const setValidationError = (error, isValid) => {
        setError(error);
        setIsValid(isValid);

        props.question.error = error;
        props.question.isValid = isValid;
    };

    const updateHcpcQuestionState = ({ acceptedRecord = null, validationReason = null }) => {
        props.question.acceptedRecord = acceptedRecord;
        props.question.validationReason = validationReason;
    };

    const handleOnInputChange = async e => {
        let { value } = e.target;

        value = value
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .replace(/\s+/g, ' ')
            .trimStart()
            .toUpperCase();

        if (value !== props.question.value) {
            setSelectedRecord(null);
            setAcceptedRecord(null);
            setValidationReason(null);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: null
            });

            handleResultsVisibility(value.length);
            updateInputValue(props.question.id, value);
        }
    };

    const handleOnInputFocus = e => {
        handleResultsVisibility(e.target.value.length);
    };

    const handleResultsVisibility = count => {
        const minCount = 3;
        const isInputActive = inputRef.current === document.activeElement;

        if (hasGmcValue()) {
            clearTimeout(debounceRef.current);
            setResultsVisible(false);
            setData([]);
            return;
        }

        if (count >= minCount && isInputActive && !isReadOnly) {
            setResultsVisible(true);

            clearTimeout(debounceRef.current);

            debounceRef.current = setTimeout(() => {
                search();
            }, 400);
        } else {
            setResultsVisible(false);
        }
    };

    const updateInputValue = async (questionId, value) => {
        await props.onChange({
            questionId,
            value
        });

        validate(false);
    };

    const search = async () => {
        setData([]);
        setDataLoading(true);
        try {
            const api = new Api();
            const response = await api.getHCPCRefData(inputRef.current.value);
            if (response.data) {
                setData(response.data);
                setFocused(-1);
            }
        } catch {
            setFocused(-1);
        } finally {
            setDataLoading(false);
        }
    };

    const handleUpdateResults = async () => {
        const record = data[focused];

        if (!record) {
            const message = 'Select a valid HCPC record from the list';

            setAcceptedRecord(null);
            setValidationReason(message);
            setValidationError(message, false);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: message
            });

            return;
        }

        if (hasGmcValue()) {
            setAcceptedRecord(null);
            setValidationReason(GMC_HCPC_ERROR);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: GMC_HCPC_ERROR
            });

            setValidationError(GMC_HCPC_ERROR, false);
            return;
        }

        setSelectedRecord(record);
        populateLinkedFields(record);
        setData([]);

        const result = validateHcpcRecord(record, props.enteredDetails || {}, {
            skipHcpcPersonValidation: props.skipHcpcPersonValidation
        });

        if (result.accepted) {
            setAcceptedRecord(record);
            setValidationReason(null);
            setValidationError(undefined, true);

            updateHcpcQuestionState({
                acceptedRecord: record,
                validationReason: null
            });
        } else {
            setAcceptedRecord(null);
            setValidationReason(result.reason);
            setValidationError(result.reason, false);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: result.reason
            });
        }
    };

    const populateLinkedFields = refData => {
        const linkedQuestions = props.question.linked_questions;

        for (const questionId in linkedQuestions) {
            updateInputValue(linkedQuestions[questionId], refData?.[questionId]);
        }

        updateInputValue(props.question.id, refData?.Registration_Name);

        if (props.handleDisable) {
            props.handleDisable({
                isReadOnly: !!refData?.Registration_Name,
                questionIds: [
                    props.question.id,
                    ...(linkedQuestions ? Object.values(linkedQuestions) : [])
                ]
            });
        }
    };

    const clearInputValue = e => {
        e.preventDefault();

        setSelectedRecord(null);
        setAcceptedRecord(null);
        setValidationReason(null);

        updateHcpcQuestionState({
            acceptedRecord: null,
            validationReason: null
        });

        updateInputValue(props.question.id, '');

        if (props.handleDisable) {
            props.handleDisable({
                isReadOnly: false,
                questionIds: [
                    props.question.id,
                    ...(props.question.linked_questions
                        ? Object.values(props.question.linked_questions)
                        : [])
                ]
            });
        }
    };

    const handleOnKeyDown = e => {
        const numFocusableItems = data.length - 1;

        // eslint-disable-next-line default-case
        switch (e.key) {
            case 'ArrowDown':
                if (focused < numFocusableItems) {
                    const newFocus = focused + 1;
                    setFocused(newFocus);
                    handleResultsScroll(newFocus);
                }
                break;
            case 'ArrowUp':
                if (focused > 0) {
                    const newFocus = focused - 1;
                    setFocused(newFocus);
                    handleResultsScroll(newFocus);
                }
                break;
            case 'Escape':
                inputRef.current.blur();
                setResultsVisible(false);
                break;
            case 'Enter':
                e.preventDefault();
                handleUpdateResults();
                break;
        }
    };

    const handleResultsScroll = focused => {
        const results = document.querySelector('#results');
        const item = document.querySelector('#results > li');
        if (results && item) {
            results.scrollTo({
                behavior: 'smooth',
                top: focused * item.getBoundingClientRect().height
            });
        }
    };

    const handleWindowClicked = e => {
        if (e.target.type !== 'text') {
            setResultsVisible(false);
        }
    };

    useEffect(() => {
        if (!selectedRecord) return;

        const result = validateHcpcRecord(selectedRecord, props.enteredDetails || {}, {
            skipHcpcPersonValidation: props.skipHcpcPersonValidation
        });

        if (result.accepted) {
            setAcceptedRecord(selectedRecord);
            setValidationReason(null);
            setValidationError(undefined, true);

            updateHcpcQuestionState({
                acceptedRecord: selectedRecord,
                validationReason: null
            });
        } else {
            setAcceptedRecord(null);
            setValidationReason(result.reason);
            setValidationError(result.reason, false);

            updateHcpcQuestionState({
                acceptedRecord: null,
                validationReason: result.reason
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.enteredDetails?.firstName,
        props.enteredDetails?.surname,
        props.enteredDetails?.gender,
        props.skipHcpcPersonValidation
    ]);

    useEffect(() => {
        const hasHcpcValue = !!String(props.question.value ?? '').trim();
        const hasQuestionError = !!props.question.error;

        if (hasHcpcValue || hasQuestionError) {
            validate(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.gmcValue]);

    const value = props.question.value ?? '';
    const hasHidden = props.question.hidden ?? true;

    return !hasHidden || (hasHidden && !props.question.hidden) ? (
        <div className={`nhsuk-u-clear ${styles.SearchWithResults}`}>
            <TextInput
                className={styles.SearchWithResultsInput}
                disabled={isReadOnly}
                error={error}
                hint={props.question.hint_text}
                id={props.id}
                inputRef={inputRef}
                label={props.question.name}
                onChange={handleOnInputChange}
                onFocus={handleOnInputFocus}
                onKeyDown={handleOnKeyDown}
                value={value}
                width="10"
                required={props.question.mandatory}
            />
            <Results
                data={data}
                focused={focused}
                handleClick={handleUpdateResults}
                setFocused={setFocused}
                visible={resultsVisible}
            />
            <ClearButton handleClick={clearInputValue} visible={isReadOnly} />
            <Loader loading={dataLoading} />
        </div>
    ) : null;
});

export default Hcpc;
